import React, { useEffect, useState, useCallback } from "react";
import axiosInstance, { handleApiError } from "../../services/axiosService";
import { toastSuccess } from "../../utils/toastUtils";
import { useModalContext } from "../../context/ModalContext";
import BuilderModal from "../../Layouts/Modal/BuilderModal";
import AppLeftSideBar from "../../components/SideBar/AppLeftSideBar";
import { useParams } from "react-router-dom";
import Loader from "../../Layouts/Loader/Loader";
import UserServiceModal from "./UserServiceModal";
import { useImmerReducer } from "use-immer";
import applicationUsersReducer from "./applicationUsersReducer";
import ButtonActionIcon from "../../components/BuilderComponents/ButtonActionIcon";
import BuilderTable from "../../Layouts/BuilderTable";
import Input from "../../components/FormComponents/Input";
import Button from "../../components/FormComponents/Button";

import Pagination from "./Pagination";
import SyncCongnitoUser from "../BuilderUsers/SyncCongnitoUser";
import { debounce } from "../../commonFunctions/arrayFunc";
import { encryptData } from "../Login/Login";
import MaterialIcon from "../../components/BuilderComponents/MaterialIcon";
import storageService from "../../services/storageService";
import { STORAGE_KEYS } from "../../constant/constant";

const initialState = {
  allUsersData: [],
  isLoader: false,
  searchQuery: "",
  modal: {
    isOpen: false,
    data: {
      email: "",
      firstName: "",
      lastName: "",
      mobile: "",
      password: ""
    }
  }
};

const ApplicationUsersSection = () => {
  const limit = storageService.get(STORAGE_KEYS.LIMIT) || 10;
  const [pagination, setPagination] = useState({
    search: undefined,
    page: 1,
    limit: Number(limit)
  });
  const [pageTrack, setPageTrack] = useState({
    currPage: undefined,
    totalPages: undefined
  });

  const { getUserConfirmationForDelete } = useModalContext();
  const [reactivationModal, setReactivationModal] = useState({
    isOpen: false,
    message: "",
    userId: "",
    payload: null
  });

  const params = useParams();
  const [state, dispatch] = useImmerReducer(applicationUsersReducer, initialState);

  const { allUsersData, isLoader, searchQuery, modal } = state;
  const { isOpen, data: editData } = modal;

  const fetchAllUsers = async (search, page, limit) => {
    try {
      dispatch({ type: "SET_LOADER", payload: true });

      const { meta, data } = await axiosInstance.get("/user-services/users", {
        params: { search: search, page: page, limit: limit }
      });
      setPageTrack({ currPage: meta.page_number, totalPages: meta.total_pages });

      dispatch({ type: "SET_USERS", payload: data });
    } catch (error) {
      handleApiError(error.response?.data?.message || "Failed to fetch users");
    } finally {
      dispatch({ type: "SET_LOADER", payload: false });
    }
  };

  // Function to handle page changes in pagination
  const onPageChangeAPI = async (pageNumber) => {
    try {
      const { limit } = pagination;
      const { data } = fetchAllUsers(pagination.search, pageNumber, limit);
      setPagination((prev) => ({ ...prev, page: pageNumber }));
      setPageTrack((prev) => (pageNumber === 1 ? { ...prev, currPage: 1 } : { ...prev, currPage: pageNumber }));

      dispatch({ type: "SET_USERS", payload: data });
    } catch (error) {
      const errorMessage = error.response?.data?.message || "Failed to fetch users";
      handleApiError(errorMessage);
    }
  };

  // Function to handle limit changes in pagination
  const onLimitChangeAPI = async (newLimit) => {
    try {
      // save limit to local storage
      storageService.set(STORAGE_KEYS.LIMIT, newLimit);

      const { page } = pagination; //default page number
      const { data } = fetchAllUsers(pagination.search, page, newLimit);

      // Update users and set new limit in pagination state
      dispatch({ type: "SET_USERS", payload: data });
      setPagination((prev) => ({ ...prev, limit: newLimit }));
    } catch (error) {
      // Handle and log errors
      const errorMessage = error.response?.data?.message || "Failed to fetch users";
      handleApiError(errorMessage);
    }
  };

  // Debounced search function
  // eslint-disable-next-line
  const debouncedSearch = useCallback(
    debounce(async (query) => {
      if (!query || query.trim().length < 1) {
        fetchAllUsers(undefined, 1, 10);

        return;
      }

      try {
        const { data } = fetchAllUsers(query, 1, pagination.limit);

        dispatch({ type: "SET_USERS", payload: data });
      } catch (error) {
        handleApiError(error.response?.data?.message || "Failed to search users");
      }
    }, 500),
    // eslint-disable-next-line
    []
  ); // eslint-disable-next-line

  const handleSearchInputChange = (e) => {
    const query = e.target.value;
    setPagination((prev) => ({ ...prev, search: query }));

    dispatch({ type: "SET_SEARCH_QUERY", payload: query });
    debouncedSearch(query);
  };

  const handleAddUser = async (userData) => {
    const decryptUserData = {
      ...userData,
      email: encryptData(userData.email, true),
      password: encryptData(userData.password, true)
    };

    try {
      if (userData.email.trim().length < 3) return;
      const { data, message } = await axiosInstance.post(`/user-services/users`, decryptUserData);
      dispatch({ type: "ADD_USER", payload: data });
      dispatch({ type: "SET_MODAL", payload: { isOpen: false, data: initialState.modal.data } });
      toastSuccess(message);
    } catch (error) {
      const errorData = error?.response?.data;
      const isReactivationNeeded = errorData?.data?.reactivate === true;

      if (isReactivationNeeded) {
        setReactivationModal({
          isOpen: true,
          message: errorData?.message || "User previously deleted. Would you like to reactivate?",
          userId: errorData.data.user_id,
          payload: decryptUserData
        });
        return;
      }

      handleApiError(errorData?.message || "Failed to add user");
    }
  };

  const handleEditUser = async (userData, id) => {
    try {
      if (userData.password === undefined || userData.password.trim().length === 0) delete userData["password"];
      const { message } = await axiosInstance.patch(`/user-services/users/${id}`, userData);
      dispatch({ type: "EDIT_USER", payload: { id, userData } });
      dispatch({ type: "SET_MODAL", payload: { isOpen: false, data: initialState.modal.data } });
      toastSuccess(message);
    } catch (error) {
      handleApiError(error.response?.data?.message || "Failed to update user");
    }
  };

  const handleDeleteUser = async (userId, name) => {
    try {
      const userConfirmed = await getUserConfirmationForDelete(name);
      if (!userConfirmed) return;
      const { message } = await axiosInstance.delete(`/user-services/users/${userId}`);
      dispatch({ type: "DELETE_USER", payload: userId });
      toastSuccess(message);
    } catch (error) {
      handleApiError(error.response?.data?.message || "Failed to delete user");
    }
  };

  const handleOpenAddUserModal = () => {
    dispatch({ type: "SET_MODAL", payload: { isOpen: true, data: initialState.modal.data } });
  };

  const handleOpenEditUserModal = (data) => {
    dispatch({ type: "SET_MODAL", payload: { isOpen: true, data } });
  };

  const closeReactivationModal = () => {
    setReactivationModal({
      isOpen: false,
      message: "",
      userId: "",
      payload: null
    });
  };

  const confirmUserReactivation = async () => {
    if (!reactivationModal.userId || !reactivationModal.payload) return;
    try {
      const { data, message } = await axiosInstance.post(
        `/user-services/users/${reactivationModal.userId}/reactivate`,
        reactivationModal.payload
      );
      dispatch({ type: "ADD_USER", payload: data });
      dispatch({ type: "SET_MODAL", payload: { isOpen: false, data: initialState.modal.data } });
      toastSuccess(message || "User reactivated successfully");
      closeReactivationModal();
    } catch (error) {
      handleApiError(error?.response?.data?.message || "Failed to reactivate user");
    }
  };

  useEffect(() => {
    var limit = storageService.get(STORAGE_KEYS.LIMIT);
    if (!limit) {
      storageService.set(STORAGE_KEYS.LIMIT, 10);
      limit = 10;
    }

    fetchAllUsers(pagination.search, 1, limit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns = [
    { key: "name", header: "Name" },
    { key: "email", header: "Email" },
    { key: "mobile", header: "Mobile" }
  ];

  const customColumns = [
    {
      header: "Actions",
      render: (user) => (
        <>
          <ButtonActionIcon type="edit" onClick={() => handleOpenEditUserModal(user)} />
          <ButtonActionIcon type="delete" onClick={() => handleDeleteUser(user.id, user.name)} />
        </>
      )
    }
  ];

  return (
    <div className="application_container">
      <AppLeftSideBar type={params.orgId ? "app" : "org"} />
      <div className="dashboard_outer_section">
        <div className="dashboard-top-header">
          <h2>Application Users</h2>
          <div className="d_flex justify_between mr_0 mx_0 gap_3">
            <div className="position_relative">
              <Input
                autoFocus
                placeholder="Search User"
                value={searchQuery}
                onChange={handleSearchInputChange}
                containerStyle={{ position: "relative", width: "100%" }}
                inputBoxStyle={{ paddingLeft: "28px", height: "36px" }}
              />
              <MaterialIcon
                name="search"
                className="search position_absolute font_size_large"
                style={{ top: "8px", left: "8px" }}
              />
            </div>
            <div className="d_flex gap_3">
              <Button onClick={handleOpenAddUserModal}>Add User</Button>
              <SyncCongnitoUser path="/user-services/users/cognito/application" />
            </div>
          </div>
        </div>

        {isLoader ? (
          <Loader width={70} />
        ) : (
          <div className="org-dashboard-page-body">
            <BuilderTable
              columns={columns}
              data={allUsersData}
              customColumns={customColumns}
              columnStyles={{
                Actions: { display: "flex", gap: "4px" }
              }}
              noDataMessage="No Users Found"
            />
            <div className="p_3 bottom_border">
              <Pagination
                pageTrack={pageTrack}
                pagination={pagination}
                onPageChange={onPageChangeAPI}
                onLimitChange={onLimitChangeAPI}
              />
            </div>
          </div>
        )}

        <BuilderModal
          isOpen={isOpen}
          onClose={() => dispatch({ type: "SET_MODAL", payload: { isOpen: false, data: initialState.modal.data } })}
          modalName={editData?.id ? "Edit User" : "Add User"}
        >
          <UserServiceModal editData={editData} addUser={handleAddUser} editUser={handleEditUser} />
        </BuilderModal>

        <BuilderModal isOpen={reactivationModal.isOpen} onClose={closeReactivationModal} modalName="Reactivate User">
          <div className="builder_form">
            <p>{reactivationModal.message}</p>
            <div className="d_flex gap_2 justify_between">
              <Button variant="plain" className="confirmation_popup_buttons" onClick={closeReactivationModal}>
                Cancel
              </Button>
              <Button className="confirmation_popup_buttons" onClick={confirmUserReactivation}>
                Reactivate
              </Button>
            </div>
          </div>
        </BuilderModal>
      </div>
    </div>
  );
};

export default ApplicationUsersSection;
